import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

import type { GprsCatalogCommand, GprsCommand } from "../domain";

const namedRefDtoSchema = z.object({
  id: z.string(),
  nameEn: z.string(),
  nameAr: z.string(),
});

export const gprsCommandDtoSchema = z.object({
  id: z.string(),
  batchId: z.string(),
  company: namedRefDtoSchema.nullish(),
  device: z.object({
    id: z.string(),
    code: z.string().nullish(),
    imei: z.string().nullish(),
  }),
  command: z
    .object({
      typeId: z.string(),
      code: z.string(),
      nameEn: z.string(),
      nameAr: z.string(),
    })
    .nullish(),
  // Values come back as text; read numbers and flags as text too.
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).nullish(),
  payload: z.string().nullish(),
  status: z.enum(["PENDING", "SUCCESSFUL", "FAILED", "TIMED_OUT"]),
  failureReason: z.string().nullish(),
  response: z.string().nullish(),
  executionMs: z.number().nullish(),
  sentAt: z.string().nullish(),
  respondedAt: z.string().nullish(),
  createdAt: z.string(),
});

export const gprsCommandsListResponseDtoSchema = z.object({
  data: z.array(gprsCommandDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const gprsCommandResponseDtoSchema = z.object({ data: gprsCommandDtoSchema });

const catalogParameterDtoSchema = z.object({
  name: z.string(),
  labelEn: z.string(),
  labelAr: z.string(),
  kind: z.string(),
  required: z.boolean(),
  minValue: z.number().nullish(),
  maxValue: z.number().nullish(),
  pattern: z.string().nullish(),
});

export const gprsCatalogResponseDtoSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      nameEn: z.string(),
      nameAr: z.string(),
      parameters: z.array(catalogParameterDtoSchema).nullish(),
      supportedDevices: z.number().nullish(),
    }),
  ),
});

export const sendGprsCommandResponseDtoSchema = z.object({
  message: z.string().nullish(),
  data: z.object({
    batchId: z.string(),
    total: z.number(),
    pending: z.number(),
    failed: z.number(),
  }),
});

export function gprsCommandFromDto(dto: z.infer<typeof gprsCommandDtoSchema>): GprsCommand {
  return {
    id: dto.id,
    batchId: dto.batchId,
    company: dto.company ?? null,
    device: { id: dto.device.id, code: dto.device.code ?? "", imei: dto.device.imei ?? "" },
    command: dto.command ?? null,
    params: Object.fromEntries(
      Object.entries(dto.params ?? {}).map(([name, value]) => [name, String(value)]),
    ),
    payload: dto.payload ?? null,
    status: dto.status,
    failureReason: dto.failureReason ?? null,
    response: dto.response ?? null,
    executionMs: dto.executionMs ?? null,
    sentAt: dto.sentAt ?? null,
    respondedAt: dto.respondedAt ?? null,
    createdAt: dto.createdAt,
  };
}

export function gprsCatalogCommandFromDto(
  dto: z.infer<typeof gprsCatalogResponseDtoSchema>["data"][number],
): GprsCatalogCommand {
  return {
    id: dto.id,
    code: dto.code,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    parameters: (dto.parameters ?? []).map((parameter) => ({
      ...parameter,
      minValue: parameter.minValue ?? null,
      maxValue: parameter.maxValue ?? null,
      pattern: parameter.pattern ?? null,
    })),
    supportedDevices: dto.supportedDevices ?? null,
  };
}
